/**
 * El asterisco de los campos obligatorios
 * -----------------------------------------------------------------------------
 * `CompanyTheme.form` ya lo pone: cuando un campo se declara `required: true`,
 * le cuelga al rótulo un `<span class="form-required">*</span>`.
 *
 * Pero **no todas las pantallas se construyen con él**. Las que tienen
 * desplegables remotos encadenados -contratar a alguien, registrar una factura-
 * están escritas a mano en la plantilla, porque el guion que las declararía sale
 * más largo que el HTML que ahorra. Esas se quedaban sin la marca, así que el
 * mismo formulario tenía unos campos con asterisco y otros sin él según cómo se
 * hubiera escrito la pantalla. Quien lo rellena no puede saber cuál es cuál.
 *
 * Aquí se recorre el documento y se marca lo que el navegador ya considera
 * obligatorio: el atributo `required`. **Una sola fuente**, la misma que decide
 * si el formulario deja enviar, así que el asterisco no puede mentir - y si
 * mañana se le quita el `required` a un campo, la marca se va sola.
 *
 * Se pone el mismo `<span class="form-required">` que pone el constructor: es
 * el que ya tiene estilo, y así los dos caminos se ven igual.
 *
 * ## Después de MDB, no antes
 *
 * MDB reescribe el DOM de los desplegables al inicializarlos -envuelve el
 * `<select>` y mueve cosas de sitio-, así que buscar el rótulo antes es buscar
 * en un árbol que va a cambiar. Se corre al final de `autoInit`.
 *
 * ## Idempotente
 *
 * Se comprueba si el rótulo ya lleva la marca antes de ponerla. Hace falta: esto
 * se vuelve a llamar sobre el HTML que llega después -un renglón nuevo de una
 * factura, una tabla que se refresca- y sin la comprobación acabarían tres
 * asteriscos seguidos.
 */

/** La misma clase que usa `form.js`, para que se vean igual. */
const MARCA = 'form-required';

/**
 * Lo que se marca: **lo mismo que comprueba el validador.**
 *
 * `comprobarNecesarios` exige dos cosas, `required` y `data-necesario` -esta
 * segunda para los campos que además dicen cómo se llaman, y para las parejas
 * «desplegable o texto» que se escriben a mano-. Aquí se miraba solo la
 * primera, así que había campos que el formulario no dejaba enviar vacíos y que
 * no llevaban asterisco: obligatorios en secreto.
 *
 * Las dos listas tienen que ser la misma o vuelven a separarse. Si mañana el
 * validador entiende una tercera forma, se añade **aquí también**.
 */
const SELECTOR = 'input[required], select[required], textarea[required], [data-necesario]';

/**
 * El rótulo de un campo, o `null`.
 *
 * Por `for` primero, que es lo explícito y lo que usa el lector de pantalla. Si
 * no lo hay -o el `id` tiene caracteres que romperían el selector- se busca el
 * `.form-label` que lo acompaña dentro del mismo grupo.
 *
 * @param {Element} campo
 * @returns {?Element}
 */
function etiquetaDe(campo) {
  const id = campo.getAttribute('id');

  if (id) {
    // Los `id` de esta suite son sencillos, pero uno con un punto o dos puntos
    // -`precio.1`- convierte el selector en otra cosa. `CSS.escape` lo evita.
    const escapado = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    const porFor = document.querySelector(`label[for="${escapado}"]`);

    if (porFor) return porFor;
  }

  // Sin `for`: el rótulo hermano. En esta suite el `<label>` va **después** del
  // campo -es lo que necesita el rótulo flotante de MDB- pero se miran los dos
  // lados, que una plantilla puede escribirlo al derecho.
  const grupo = campo.closest('.form-outline, .form-group, .col-12, .col-md-6') || campo.parentElement;

  return grupo ? grupo.querySelector('.form-label') : null;
}

/**
 * Marca los campos obligatorios que haya dentro de `root`.
 *
 * @param {ParentNode} [root=document]
 */
export function marcarObligatorios(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root
    .querySelectorAll(SELECTOR)
    .forEach((campo) => {
      // Los ocultos no se ven, así que no hay rótulo que marcar. Y el campo
      // nativo de fichero se esconde a propósito -viaja en el `FormData`- con su
      // propio control encima.
      if (campo.type === 'hidden') return;

      const etiqueta = etiquetaDe(campo);

      if (!etiqueta || etiqueta.querySelector(`.${MARCA}`)) return;

      const marca = document.createElement('span');
      marca.className = MARCA;
      marca.textContent = '*';
      // Fuera del árbol de accesibilidad: el `required` ya se lo dice al lector
      // de pantalla, y leer «asterisco» detrás de cada rótulo es ruido.
      marca.setAttribute('aria-hidden', 'true');

      etiqueta.appendChild(marca);
    });
}

export default marcarObligatorios;
